import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import RootFractionInput, {
  parseRootFractionInput as parseRoot,
  formatLatexPreview,
} from "../../../../components/inputs/RootFractionInput";

const DEC_TOL = 0.01;
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

// rozkład przez największy kwadrat: sqrt(n) = k*sqrt(m), m squarefree
function simplifySqrt(n) {
  if (n <= 0) return { k: 0, m: 0 };
  let k = 1, m = n;
  for (let p = 2; p * p <= m; p++) {
    while ((m % (p * p)) === 0) {
      k *= p;
      m /= (p * p);
    }
  }
  return { k, m };
}
const sqrtLatex = (n) => `\\sqrt{${n}}`;
const kSqrtMLatex = ({ k, m }) => {
  if (m === 1) return `${k}`;                 // liczba całkowita
  if (k === 1) return `\\sqrt{${m}}`;         // bez „1·”
  return `${k}\\sqrt{${m}}`;
};

/** GENERATOR:
 * a=|PA|, b=|PB| całkowite 3..30, a<b, P na zewnątrz.
 * Czasem daje wynik całkowity, czasem k√m.
 */
export function generateTask() {
  const A = Array.from({ length: 28 }, (_, i) => i + 3);   // 3..30
  for (let tries = 0; tries < 500; tries++) {
    const a = pick(A);
    let b = pick(A);
    if (b <= a) continue;

    const S = a * b; // |PC|^2
    if (!(S > 9 && S < 2000)) continue;

    const { k, m } = simplifySqrt(S);
    const pcLatex = kSqrtMLatex({ k, m });
    const pcValue = Math.sqrt(S);

    return {
      type: "FindPCFromSecantTangentTask",
      data: {
        a, b, // PA, PB
        S,
        k, m,
        pcValue,
        pcLatex,                // uproszczona postać: k√m lub liczba / √m
        theoremLatex: "PA\\cdot PB=PC^2",
      },
    };
  }

  // Fallback: a=6, b=10 ⇒ |PC| = 2√15
  const a = 6, b = 10, S = a * b;
  const { k, m } = simplifySqrt(S);
  return {
    type: "FindPCFromSecantTangentTask",
    data: {
      a, b, S, k, m,
      pcValue: Math.sqrt(S),
      pcLatex: kSqrtMLatex({ k, m }),
      theoremLatex: "PA\\cdot PB=PC^2",
    },
  };
}

/* ===== TREŚĆ ===== */
export function renderTask({ data }) {
  const { a, b } = data;
  return (
    <div style={{ marginTop: 8, marginBottom:8 }}>
      Z punktu <InlineMath math={'P'} /> poprowadzono sieczną przecinającą okrąg w punktach{" "}
      <InlineMath math={'A'} /> i <InlineMath math={'B'} /> oraz styczną w punkcie{" "}
      <InlineMath math={'C'} />. Wiadomo, że{" "}
      <InlineMath math={`PA=${a}`} /> oraz <InlineMath math={`PB=${b}`} />.
      Oblicz długość odcinka <InlineMath math={'PC'} />.
    </div>
  );
}

/* ===== INPUT ===== */
export function renderInput(_task, userAnswer, onChange, disabled) {
  const raw = typeof userAnswer === "string" ? userAnswer : userAnswer?.raw ?? "";
  const normalize = (v) => (typeof v === "string" ? v : v?.raw ?? "");
  return (
    <RootFractionInput
      value={raw}
      onChange={(v) => onChange({ raw: normalize(v) })}
      disabled={disabled}
      label="PC"
      placeholder="np. 2√15, sqrt(60), 4"
    />
  );
}

/* ===== CHECK ===== */
export function checkAnswer({ data }, userAnswer) {
  const raw = (typeof userAnswer === "string" ? userAnswer : userAnswer?.raw ?? "");
  if (!raw) return false;
  const val = parseRoot(raw);          // akceptuje też 2√15
  if (!Number.isFinite(val)) return false;

  const expected = data.pcValue;       // √(a·b)
  return Math.abs(val - expected) <= DEC_TOL;
}

/* ===== ANSWER ===== */
export function renderAnswer({ data }) {
  return <InlineMath math={`|PC|=${data.pcLatex}`} />;
}

/* ===== EXPLANATION ===== */
export function renderExplanation({ data }) {
  const { a, b, S, k, m } = data;

  // Krok 3 – pokaż uproszczenie tylko jeśli naprawdę coś się zmienia:
  // - jeśli m === 1 → PC = √S = k
  // - jeśli k === 1 i m > 1 → PC = √S (bez "= 1√m")
  // - jeśli k > 1 i m > 1 → PC = √S = k√m
  let step3;
  if (m === 1) {
    step3 = `|PC|=\\sqrt{${S}}=${k}`;
  } else if (k === 1) {
    step3 = `|PC|=\\sqrt{${S}}`;
  } else {
    step3 = `|PC|=\\sqrt{${S}}=${k}\\,\\sqrt{${m}}`;
  }

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div>
        <strong>Krok 1.</strong> Z twierdzenia o odcinkach siecznej i stycznej:
        <InlineMath math={'\\;|PA|\\cdot |PB| = |PC|^2'} />.
      </div>

      <div style={{ marginTop: 8 }}>
        <strong>Krok 2.</strong> Podstawiamy dane.
      </div>
      <BlockMath math={`${a}\\cdot${b} = |PC|^2`} />
      <BlockMath math={`|PC|^2 = ${S}`} />

      <div style={{ marginTop: 8 }}>
        <strong>Krok 3.</strong> Pierwiastkujemy i (jeśli się da) upraszczamy.
      </div>
      <BlockMath math={step3} />
    </div>
  );
}

/* ===== PREVIEW UŻYTKOWNIKA ===== */
export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === "string" ? userAnswer : userAnswer?.raw ?? "";
  if (!raw) return <i>(brak odpowiedzi)</i>;
  const latex = formatLatexPreview(raw, "|PC|");
  return <InlineMath math={latex} />;
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
